import type {
  CatalogPlayer,
  CatalogTeamBlock,
  EventType,
  ProjectState,
} from "@livcg/schema";
import { coachSide, isCardEventType } from "@livcg/schema";
import { ArrowLeftRight, ChevronDown, Square, Target } from "lucide-react";
import { useImperativeHandle, useState } from "react";
import { Button, cn, focusRing } from "../../ui/index.js";
import { byNumber } from "../rosterOrder.js";
import { useMatchDetail } from "../useMatchDetail.js";
import { Panel } from "./Panel.js";
import { SubstitutionModal } from "./SubstitutionModal.js";
import { useSubDraft } from "./subDraft.js";
import { useQuickEventChain } from "./useQuickEventChain.js";

const CARD_TRIGGERS: {
  type: EventType;
  label: string;
  icon: typeof Target;
  variant: "warning" | "live";
}[] = [
  { type: "yellow_card", label: "SARIQ", icon: Square, variant: "warning" },
  { type: "red_card", label: "QIZIL", icon: Square, variant: "live" },
];

const GOAL_MENU: { type: EventType; label: string }[] = [
  { type: "goal", label: "Oddiy" },
  { type: "penalty_goal", label: "Penalti" },
  { type: "own_goal", label: "Avtogol" },
];

/** The list to display: the matchday roster if there is one (starters first,
 *  each by shirt number), otherwise all of the team's players. */
function displayList(block: CatalogTeamBlock | undefined): CatalogPlayer[] {
  if (!block) return [];
  const roster = block.roster ?? [];
  if (roster.length > 0) {
    return [
      ...roster.filter((r) => r.isStarter).sort(byNumber),
      ...roster.filter((r) => !r.isStarter).sort(byNumber),
    ].map((r) => ({
      id: r.playerId,
      firstName: r.firstName,
      lastName: r.lastName,
      number: r.number,
      isStarter: r.isStarter,
    }));
  }
  return [...block.players].sort(byNumber);
}

/** Imperative handle: the keyboard shortcuts (G/Y/R/S) apply to the selected
 *  row's player — with no selection, the first one in the home list. */
export type QuickEventsHandle = { fire: (type: EventType) => void };

type Props = {
  slug: string;
  state: ProjectState;
  controlRef?: React.Ref<QuickEventsHandle>;
  /** Klassik wide mode: home/away side by side, lists in full without scroll. */
  wide?: boolean;
  /** Deck passes its own chain so its tiles and this panel share the timers. */
  chain?: ReturnType<typeof useQuickEventChain>;
};

export function QuickEvents({ slug, state, controlRef, wide, chain: sharedChain }: Props) {
  const md = useMatchDetail(state);
  const [selected, setSelected] = useState<null | { side: "home" | "away"; playerId: string }>(
    null,
  );

  /** 2nd yellow: if the player already has a yellow, we ask for confirmation
   *  before turning it into a QIZIL. */
  const [pendingSecondYellow, setPendingSecondYellow] = useState<null | {
    side: "home" | "away";
    playerId: string;
    name: string;
  }>(null);

  const ownChain = useQuickEventChain(slug, state);
  const chain = sharedChain ?? ownChain;
  const activeChain = chain.activeChain;
  const [subModal, setSubModal] = useState<null | "home" | "away">(null);
  const [homeDraft] = useSubDraft(slug, "home");
  const [awayDraft] = useSubDraft(slug, "away");

  const blockFor = (side: "home" | "away") => (side === "home" ? md.detail?.home : md.detail?.away);
  const findPlayer = (side: "home" | "away", playerId: string) =>
    coachSide(playerId) === side
      ? md.coach(side)
      : displayList(blockFor(side)).find((p) => p.id === playerId);

  const isSentOff = (playerId: string) => !!state.match.playerCards?.[playerId]?.sentOff;

  const fireFor = (type: EventType, side: "home" | "away", playerId: string) => {
    if (coachSide(playerId) && !isCardEventType(type)) return;
    setSelected({ side, playerId });
    // Sent off (2nd yellow / red): no further actions — only taking the
    // banner that is still on air back off (the event stays in the log).
    // The 2nd yellow banner is shown on the yellow button.
    if (type === "yellow_card" && chain.toggleOff("second_yellow", side, playerId)) return;
    if (isSentOff(playerId)) {
      if (type !== "substitution") chain.toggleOff(type, side, playerId);
      return;
    }
    const block = blockFor(side);
    const player = findPlayer(side, playerId);
    if (!block || !player) {
      // even if the roster has changed, the on-air banner must be closed.
      if (type !== "substitution")
        chain.toggleOff(type as Exclude<EventType, "substitution">, side, playerId);
      return;
    }
    // 2nd yellow: if the player already has a yellow, we ask for confirmation.
    if (type === "yellow_card") {
      const pc = state.match.playerCards?.[playerId];
      if (pc && pc.yellows >= 1 && chain.activeChain !== `yellow_card:${side}:${playerId}`) {
        setPendingSecondYellow({ side, playerId, name: player.lastName || player.firstName });
        return;
      }
    }
    if (chain.toggleOff(type as Exclude<EventType, "substitution">, side, playerId)) return;
    chain.fire(type as Exclude<EventType, "substitution">, side, player, block.team);
  };

  useImperativeHandle(controlRef, () => ({
    fire: (type: EventType) => {
      const sel =
        selected ??
        (() => {
          const first = displayList(md.detail?.home)[0];
          return first ? { side: "home" as const, playerId: first.id } : null;
        })();
      if (sel) fireFor(type, sel.side, sel.playerId);
    },
  }));

  const selectedPlayer = selected ? findPlayer(selected.side, selected.playerId) : undefined;

  return (
    <>
      <Panel
        label="Tez hodisa"
        action={
          <span className="max-w-[130px] truncate text-[10.5px] text-ink-faint">
            {selectedPlayer
              ? `${selectedPlayer.number ? `#${selectedPlayer.number} ` : ""}${selectedPlayer.lastName || selectedPlayer.firstName}`
              : "O'yinchi tanlanmagan"}
          </span>
        }
      >
        <div className={cn(wide ? "grid grid-cols-2 items-start gap-3" : "flex flex-col gap-3")}>
          <div className={cn("flex gap-1.5", wide && "col-span-2")}>
            <Button
              variant="secondary"
              size="xs"
              leadingIcon={<ArrowLeftRight className="h-3 w-3" />}
              onClick={() => setSubModal("home")}
              title={homeDraft.length > 0 ? `${homeDraft.length} ta almashinuv tayyor turibdi` : undefined}
            >
              Sub H
              {homeDraft.length > 0 && (
                <span className="ml-1 rounded-full bg-warning px-1.5 text-[10px] font-bold text-canvas">
                  {homeDraft.length}
                </span>
              )}
            </Button>
            <Button
              variant="secondary"
              size="xs"
              leadingIcon={<ArrowLeftRight className="h-3 w-3" />}
              onClick={() => setSubModal("away")}
              title={awayDraft.length > 0 ? `${awayDraft.length} ta almashinuv tayyor turibdi` : undefined}
            >
              Sub A
              {awayDraft.length > 0 && (
                <span className="ml-1 rounded-full bg-warning px-1.5 text-[10px] font-bold text-canvas">
                  {awayDraft.length}
                </span>
              )}
            </Button>
          </div>
          <SideList
            side="home"
            expanded={wide}
            block={md.detail?.home}
            coach={md.coach("home")}
            isSentOff={isSentOff}
            selectedId={selected?.side === "home" ? selected.playerId : null}
            activeChain={activeChain}
            onSelect={(id) => setSelected({ side: "home", playerId: id })}
            onTrigger={(type, id) => fireFor(type, "home", id)}
          />
          <SideList
            side="away"
            expanded={wide}
            block={md.detail?.away}
            coach={md.coach("away")}
            isSentOff={isSentOff}
            selectedId={selected?.side === "away" ? selected.playerId : null}
            activeChain={activeChain}
            onSelect={(id) => setSelected({ side: "away", playerId: id })}
            onTrigger={(type, id) => fireFor(type, "away", id)}
          />
          {pendingSecondYellow && (
            <div
              className={cn(
                "rounded-md border border-live/50 bg-live-soft/40 p-2.5",
                wide && "col-span-2",
              )}
            >
              <div className="mb-2 text-[11px] font-semibold text-ink">
                ⚠ {pendingSecondYellow.name} — 2-sariq → QIZIL. Tasdiqlaysizmi?
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  variant="live"
                  size="sm"
                  className="w-full uppercase tracking-[0.04em]"
                  onClick={() => {
                    const { side, playerId } = pendingSecondYellow;
                    const block = blockFor(side);
                    const player = findPlayer(side, playerId);
                    if (block && player) chain.fire("second_yellow", side, player, block.team);
                    setPendingSecondYellow(null);
                  }}
                >
                  Ha, 2-sariq → qizil
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => setPendingSecondYellow(null)}
                >
                  Bekor
                </Button>
              </div>
            </div>
          )}
        </div>
      </Panel>
      {subModal && (
        <SubstitutionModal
          slug={slug}
          state={state}
          side={subModal}
          open={subModal !== null}
          onClose={() => setSubModal(null)}
          onFire={(side, pairs) => {
            const block = blockFor(side);
            if (block) chain.fireSubGroup(side, pairs, block.team);
          }}
        />
      )}
    </>
  );
}

function SideList({
  side,
  block,
  coach,
  isSentOff,
  selectedId,
  activeChain,
  expanded,
  onSelect,
  onTrigger,
}: {
  side: "home" | "away";
  block: CatalogTeamBlock | undefined;
  /** Booked like a bench player: only SARIQ/QIZIL, no GOL. */
  coach: CatalogPlayer;
  isSentOff: (playerId: string) => boolean;
  selectedId: string | null;
  activeChain: string | null;
  /** true — full list without scroll (Klassik wide mode). */
  expanded?: boolean | undefined;
  onSelect: (id: string) => void;
  onTrigger: (type: EventType, playerId: string) => void;
}) {
  const players = displayList(block);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const hasBench = players.some((p) => p.isStarter === false);
  const benchStart = players.findIndex((p) => p.isStarter === false);

  return (
    <div className="rounded-md border border-divider bg-surface-muted/40 p-2">
      <div className="mb-1.5 flex items-center justify-between gap-2 px-1">
        <span className="text-[11px] font-semibold text-ink">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">
            {side === "home" ? "Home" : "Away"}
          </span>{" "}
          {block?.team.shortName ?? "—"}
        </span>
        <span className="text-[9.5px] tabular-nums text-ink-faint">{players.length}</span>
      </div>
      <div className={cn("flex flex-col gap-px pr-0.5", !expanded && "max-h-52 overflow-y-auto")}>
        {players.length === 0 && (
          <div className="px-1 py-2 text-[11px] text-ink-faint">O'yinchilar yo'q</div>
        )}
        {players.map((p, i) => {
          const off = isSentOff(p.id);
          const goalOnAir = activeChain === `goal:${side}:${p.id}`;
          return (
            <div key={p.id} data-anchor={`player:${p.id}`}>
              {hasBench && i === benchStart && (
                <div className="mt-1 mb-0.5 flex items-center gap-2 px-1 text-[9px] font-semibold uppercase tracking-wider text-ink-faint">
                  Zaxira
                  <span className="h-px flex-1 bg-divider" />
                </div>
              )}
              <div
                className={cn(
                  "flex items-center gap-1 rounded-md px-1 py-0.5",
                  selectedId === p.id && "bg-surface-elevated",
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelect(p.id)}
                  className={cn(
                    focusRing,
                    "h-7 min-w-0 flex-1 truncate rounded px-1.5 text-left text-[12px] transition-colors hover:bg-surface-hover",
                    p.isStarter === false ? "text-ink-muted" : "text-ink",
                    off && "text-ink-faint line-through",
                  )}
                  title={`${p.firstName} ${p.lastName}${off ? " — chetlatilgan" : ""}`}
                >
                  <span className="mr-1 inline-block w-6 text-right font-mono text-[10.5px] tabular-nums text-ink-faint">
                    {p.number ?? "—"}
                  </span>
                  {p.lastName || p.firstName}
                </button>
                <div className="relative flex shrink-0 items-center gap-1">
                  <Button
                    variant="success"
                    size="sm"
                    iconOnly
                    disabled={off && !goalOnAir}
                    onClick={() => onTrigger("goal", p.id)}
                    title={goalOnAir ? "GOL — efirdan olish" : "GOL (oddiy)"}
                    aria-label={`GOL — ${p.lastName || p.firstName}`}
                    aria-pressed={goalOnAir}
                    className={cn(
                      goalOnAir && "ring-2 ring-live ring-offset-1 ring-offset-surface",
                    )}
                  >
                    <Target className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    disabled={off}
                    onClick={() => setOpenMenu(openMenu === p.id ? null : p.id)}
                    title="Gol turi"
                    aria-label="Gol turini tanlash"
                  >
                    <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
                  </Button>
                  {openMenu === p.id && !off && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                      <div className="absolute right-0 top-full z-20 mt-1 w-32 rounded-md border border-border bg-surface p-1 shadow-xl">
                        {GOAL_MENU.map((g) => (
                          <button
                            key={g.type}
                            type="button"
                            className="block w-full rounded px-2 py-1 text-left text-[12px] text-ink-soft hover:bg-surface-hover"
                            onClick={() => {
                              setOpenMenu(null);
                              onTrigger(g.type, p.id);
                            }}
                          >
                            {g.label}
                          </button>
                        ))}
                        <div className="mt-1 border-t border-divider px-2 pt-1 text-[9.5px] leading-tight text-ink-faint">
                          Avtogol: o'yinchi o'z jamoasidan, ochko raqibga
                        </div>
                      </div>
                    </>
                  )}
                  <CardButtons
                    side={side}
                    player={p}
                    locked={off}
                    activeChain={activeChain}
                    onTrigger={onTrigger}
                  />
                </div>
              </div>
            </div>
          );
        })}
        {block && (
          <>
            <div className="mt-1 mb-0.5 flex items-center gap-2 px-1 text-[9px] font-semibold uppercase tracking-wider text-ink-faint">
              Murabbiy
              <span className="h-px flex-1 bg-divider" />
            </div>
            <div
              className={cn(
                "flex items-center gap-1 rounded-md px-1 py-0.5",
                selectedId === coach.id && "bg-surface-elevated",
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(coach.id)}
                className={cn(
                  focusRing,
                  "h-7 min-w-0 flex-1 truncate rounded px-1.5 text-left text-[12px] text-ink-muted transition-colors hover:bg-surface-hover",
                  isSentOff(coach.id) && "text-ink-faint line-through",
                )}
                title={`${coach.firstName} ${coach.lastName}`.trim()}
              >
                <span className="mr-1 inline-block w-6" />
                {coach.lastName || coach.firstName}
              </button>
              <div className="flex shrink-0 items-center gap-1">
                <CardButtons
                  side={side}
                  player={coach}
                  locked={isSentOff(coach.id)}
                  activeChain={activeChain}
                  onTrigger={onTrigger}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** `locked` — sent off: only the button whose banner is still on air stays
 *  pressable (to take it back off); a 2nd yellow shows on the yellow button. */
function CardButtons({
  side,
  player,
  locked,
  activeChain,
  onTrigger,
}: {
  side: "home" | "away";
  player: CatalogPlayer;
  locked: boolean;
  activeChain: string | null;
  onTrigger: (type: EventType, playerId: string) => void;
}) {
  return CARD_TRIGGERS.map((t) => {
    const Icon = t.icon;
    const onAir =
      activeChain === `${t.type}:${side}:${player.id}` ||
      (t.type === "yellow_card" && activeChain === `second_yellow:${side}:${player.id}`);
    return (
      <Button
        key={t.type}
        variant={t.variant}
        size="sm"
        iconOnly
        disabled={locked && !onAir}
        onClick={() => onTrigger(t.type, player.id)}
        title={onAir ? `${t.label} — efirdan olish` : t.label}
        aria-label={`${t.label} — ${player.lastName || player.firstName}`}
        aria-pressed={onAir}
        className={cn(onAir && "ring-2 ring-live ring-offset-1 ring-offset-surface")}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
      </Button>
    );
  });
}
